import {
	Component,
	Element,
	Event,
	EventEmitter,
	forceUpdate,
	h,
	Host,
	Method,
	Prop,
	State,
	Watch,
} from '@stencil/core';

import Bind from 'bind-decorator';
import { ZdsInputElement, ZdsInputViewValueFormatterFunction } from '../../types';
import { AriaObject, processAriaObject } from '../../utils/aria-object';
import { generateCssClasses } from '../../utils/generate-css-classes';
import { generateUniqueUuid } from '../../utils/generate-unique-uuid';
import { genericRenderLabel } from '../../utils/generic-render-label';
import {
	onGenericSubInputBlur,
	onGenericSubInputChange,
	onGenericSubInputFocus,
	onGenericSubInputInput,
} from '../../utils/generic-sub-input-events';
import {
	addValidation,
	getValidationCustomMessages,
	getValidationState,
	getVisualValidityStatus,
	validate,
	validateMaxLength,
	validateMinLength,
	ValidationEvent,
	ValidationFunction,
	ValidationMessages,
	ValidationState,
	VisualValidityStatus,
} from '../../utils/input-validation';
import { isSlottedInto } from '../../utils/slotted';
import { parseSpecialBooleanProp } from '../../utils/special-boolean-prop';
import { getThresholdValue } from '../../utils/threshold-value';
import { ForceInvalidType } from './force-invalid-type';

@Component({
	shadow: true,
	styleUrl: 'zds-input-text.scss',
	tag: 'zds-input-text',
})
export class ZdsInputText implements ZdsInputElement {
	contentContainerElement?: HTMLZdsInputContentContainerElement;
	inputElement?: HTMLInputElement;
	lastTabPressDate: Date;
	showThreshold: number;
	validationMessages: ValidationMessages;
	validationFunctions: ValidationFunction[] = [() => getValidationState(this.inputElement!.validity)];
	valueBeforeFocus: string | null = null;
	viewValueFormatters: ZdsInputViewValueFormatterFunction[] = [];
	warnThreshold: number;

	@Element() hostElement: HTMLElement;

	@Event() zdsBlur: EventEmitter;

	@Event({ bubbles: true }) zdsChange: EventEmitter;
	@Event({ bubbles: true }) zdsClick: EventEmitter; // Not documented for now, since it does not have many use cases and is not supported in variants like input-number
	@Event() zdsFocus: EventEmitter;
	@Event({ bubbles: true }) zdsInput: EventEmitter;
	@Event({ bubbles: true }) zdsInputDidUpdate: EventEmitter;
	@Event({ bubbles: true }) zdsFormDoSubmit: EventEmitter;
	@Event({ bubbles: true }) zdsValidate: EventEmitter<ValidationEvent>;

	@Prop() autocomplete: string;
	@Prop() componentTabIndex: number = 0; // Note: name tabIndex is reserved in HTML
	@Prop() clearable: boolean = false;
	@Prop() counterShowThreshold: number | string;
	@Prop() counterWarnThreshold: number | string;
	@Prop() disabled: boolean = false;
	@Prop() noAsterisk: boolean = false;
	@Prop() fitHeight: boolean = false;
	@Prop() forceInvalid: ForceInvalidType = ForceInvalidType.None; // Intentionally not documented; used to force invalid state without error message from a parent component
	@Prop() includeInForm: boolean = true; // Internal prop for not showing the input in the form result
	@Prop() inputAria: AriaObject | string;
	@Prop() inputmode: 'decimal' | 'email' | 'numeric' | 'search' | 'tel' | 'text' | 'url';
	@Prop() instantSave: boolean | 'no-border' = false;
	@Prop() label: string;
	@Prop() maxlength: number;
	@Prop() minlength: number;
	@Prop() name: string;
	@Prop() pattern: string;
	@Prop() placeholder: string;
	@Prop() plaintext: boolean;
	@Prop() readonly: boolean;
	@Prop() required: boolean;
	@Prop() silentErrors: boolean = false; // Internal prop used for hiding the errors
	@Prop() type: string = 'text';
	@Prop({ mutable: true }) value: string = '';
	@Prop({ reflect: true }) zdsId: string = generateUniqueUuid('zds-input-text');

	@State() focused: boolean = false;
	@State() touched: boolean = false;
	@State() validationState: ValidationState = {
		message: '',
		reason: '',
		valid: false,
	};
	@State() viewValue: string = this.value;

	@Watch('value')
	onValueChange(newValue: string) {
		if (newValue === undefined || this.value === undefined) {
			return;
		}

		if (newValue !== this.inputElement?.value) {
			this.updateViewValue();
		}
	}

	@Watch('instantSave')
	onInstantSaveChange(newValue: ZdsInputText['instantSave']) {
		this.instantSave = parseSpecialBooleanProp(newValue);
	}

	@Watch('required')
	onRequiredChange(newValue: boolean) {
		this.inputElement!.required = newValue;
		this.validate();
	}

	@Watch('counterShowThreshold')
	onCounterShowThresholdChange() {
		this.showThreshold = getThresholdValue(this.counterShowThreshold, this.maxlength, this.warnThreshold);
	}

	@Watch('counterWarnThreshold')
	onCounterWarnThresholdChange() {
		this.warnThreshold = getThresholdValue(this.counterWarnThreshold, this.maxlength);
	}

	@Watch('maxlength')
	onMaxLengthChange() {
		this.warnThreshold = getThresholdValue(this.counterWarnThreshold, this.maxlength);
		this.showThreshold = getThresholdValue(this.counterShowThreshold, this.maxlength, this.warnThreshold);
	}

	@Watch('noAsterisk')
	onNoAsteriskChange(newValue: ZdsInputText['noAsterisk']) {
		this.noAsterisk = parseSpecialBooleanProp(newValue);
	}

	@Method()
	async addViewValueFormatter(formatter: ZdsInputViewValueFormatterFunction) {
		if (this.viewValueFormatters.includes(formatter)) {
			return;
		}

		this.viewValueFormatters.push(formatter);
		this.updateViewValue();
	}

	@Method()
	async addValidationMessages(messages: ValidationMessages) {
		this.validationMessages = { ...this.validationMessages, ...messages };
	}

	@Method()
	async blurComponent() {
		this.inputElement!.blur();
	}

	@Method()
	async focusComponent(options?: FocusOptions) {
		this.inputElement!.focus(options);
	}

	// Internal method, not documented
	@Method()
	async getInputElement() {
		return this.inputElement;
	}

	@Method()
	async select() {
		this.inputElement!.select();
	}

	@Method()
	async reset() {
		this.value = this.hostElement.getAttribute('value') || '';
		this.touched = false;

		await this.validate();
	}

	@Method()
	async validate(forceTouched?: boolean) {
		this.touched = forceTouched ?? this.touched;

		this.validationState = validate(
			this.validationFunctions,
			this.validationMessages,
			this.value,
			this.viewValue,
			this.disabled,
			this.required,
		);

		/*
		 * Do not trigger validation on zds-input-text if it's coming from zds-input-code.
		 */
		const parentElement = this.hostElement.closest('div');
		if (parentElement && parentElement.classList.contains('zds-input-code__inputs')) {
			return;
		}

		if (this.validationState.valid) {
			const event = this.zdsValidate.emit({
				value: this.value,
				viewValue: this.viewValue,
			});
			if (event.detail.validationState) {
				this.validationState = event.detail.validationState;

				if (!this.validationState.valid && this.validationMessages[this.validationState.reason]) {
					this.validationState.message = this.validationMessages[this.validationState.reason];
				}
			}
		}

		this.contentContainerElement?.updateStyles();

		return this.validationState.valid;
	}

	@Bind
	acceptCurrentChange() {
		this.forceBlur();
	}

	@Bind
	clearValue() {
		this.value = '';
		this.zdsChange.emit(this.value);
		this.zdsInput.emit(this.value);

		// Focus the component, but if there is a value before focus already, don't change it
		const storedValueBeforeFocus = this.valueBeforeFocus;
		this.focusComponent();
		if (storedValueBeforeFocus !== null) {
			this.valueBeforeFocus = storedValueBeforeFocus;
		}
	}

	@Bind
	async onBlur(event: FocusEvent, force: boolean = false) {
		event.stopPropagation();

		// Prevent blur event when clicking on elements inside the zds-input-text, such as the clear button
		const isClickInsideShadowOrSlot =
			event.relatedTarget &&
			(this.hostElement.shadowRoot?.contains(event.relatedTarget as Node) ||
				isSlottedInto(event.relatedTarget as Element, this.hostElement, 'input-start') ||
				isSlottedInto(event.relatedTarget as Element, this.hostElement, 'input-end'));

		// Do blur when using tab navigatiom
		const isBlurFromTabPress = this.lastTabPressDate && new Date().getTime() - this.lastTabPressDate.getTime() < 10;

		if (isClickInsideShadowOrSlot && !isBlurFromTabPress && !force) {
			// the focus will reset the valueBeforeFocus, we cache it and reset it after the focus.
			const cachedBeforeFocus = this.valueBeforeFocus;
			this.focusComponent();
			this.valueBeforeFocus = cachedBeforeFocus;
			return;
		}

		await this.validate();
		this.updateViewValue();
		onGenericSubInputBlur(this, event);
		this.valueBeforeFocus = null;

		forceUpdate(this);
	}

	@Bind
	async onChange(event: Event) {
		this.value = this.inputElement!.value;
		await this.validate();
		onGenericSubInputChange(this, event);
	}

	@Bind
	onClick(event: MouseEvent) {
		event.stopPropagation();
		this.zdsClick.emit();
	}

	@Bind
	onFocus(event: FocusEvent) {
		this.valueBeforeFocus = this.value;
		this.updateViewValue(false);
		onGenericSubInputFocus(this, event);
	}

	@Bind
	async onInput(event: InputEvent) {
		this.value = this.inputElement!.value;
		await this.validate();
		onGenericSubInputInput(this, event);
	}

	@Bind
	onKeyDown(event: KeyboardEvent) {
		if (this.instantSave) {
			switch (event.key) {
				case 'Enter':
					this.acceptCurrentChange();
					break;
				case 'Escape':
					this.rejectCurrentChange();
					break;
			}
			return;
		}

		switch (event.key) {
			case 'Enter':
				// If this input is inside a form, submit it
				this.zdsFormDoSubmit.emit();
				break;
			case 'Tab':
				this.lastTabPressDate = new Date();
				break;
		}
	}

	connectedCallback(): void {
		this.inputDidUpdate();
	}

	componentWillLoad(): void {
		// Force initial call of the instantSave watcher
		this.onInstantSaveChange(this.instantSave);

		this.validationMessages = getValidationCustomMessages(this.hostElement);

		this.warnThreshold = getThresholdValue(this.counterWarnThreshold, this.maxlength);
		this.showThreshold = getThresholdValue(this.counterShowThreshold, this.maxlength, this.warnThreshold);

		// Add min/max length validation
		addValidation(this.validationFunctions, validateMinLength.bind(this));
		addValidation(this.validationFunctions, validateMaxLength.bind(this));
	}

	componentDidUpdate(): void {
		this.inputDidUpdate();
	}

	/** Blur the input and emit a blur event */
	forceBlur() {
		if (this.hostElement.shadowRoot?.activeElement === this.inputElement) {
			// If the input has focus, remove it
			// Blur event is automatically emitted
			this.inputElement!.blur();
		} else {
			// If the input element does not have focus, force emit of a blur element
			const dummyBlurEvent = new FocusEvent('dummyBlurEvent');
			this.onBlur(dummyBlurEvent, true);
		}
	}

	getSlotNodeName(slotName: string) {
		const slot = this.hostElement.querySelector(`:scope > [slot="${slotName}"]`);
		const nodeName = slot?.nodeName.toLowerCase();
		if (nodeName !== 'slot') return nodeName;

		// The slotted element is a slot itself, so return the name of its first slotted node
		const subNodes = (slot as HTMLSlotElement).assignedNodes();
		if (subNodes.length === 0) return null;
		return subNodes[0].nodeName.toLowerCase();
	}

	@Bind
	rejectCurrentChange() {
		this.value = this.valueBeforeFocus ?? '';
		this.forceBlur();
	}

	render() {
		const inputClasses = generateCssClasses('zds-input-text', {
			fitHeight: this.fitHeight,
			fitHeightWithLabel: this.fitHeight && this.label ? true : false,
		});

		const hostStyle = {
			height: this.fitHeight && '100%',
		};

		return (
			<Host style={hostStyle}>
				<div class={inputClasses}>
					{genericRenderLabel(
						this.hostElement,
						this.label,
						`${this.zdsId}-input`,
						{
							class: 'zds-input-text__label',
						},
						this.required,
						this.noAsterisk,
					)}
					{this.renderError()}
					{this.renderContent()}
					{this.renderCounter()}
				</div>
			</Host>
		);
	}

	renderContent() {
		// Determine valid/invalid status of content wrapper
		let invalidType: ForceInvalidType;
		if (this.forceInvalid !== ForceInvalidType.None) {
			invalidType = this.forceInvalid;
		} else if (getVisualValidityStatus(this) === VisualValidityStatus.InvalidUnfocused) {
			invalidType = ForceInvalidType.WithStatusIcon;
		} else {
			invalidType = ForceInvalidType.None;
		}

		return (
			<zds-input-content-container
				clearable={this.clearable}
				disabled={this.disabled || undefined}
				fitHeight={this.fitHeight}
				focused={this.focused}
				hasValue={this.value !== ''}
				instantSave={this.instantSave}
				invalidType={invalidType}
				type={this.type}
				onZdsAccept={this.acceptCurrentChange}
				onZdsClear={this.clearValue}
				onZdsClick={(event) => this.onClick(event.detail)}
				onZdsShouldFocus={() => void this.focusComponent()}
				onZdsReject={this.rejectCurrentChange}
				plaintext={this.plaintext}
				readonly={this.readonly}
				silentErrors={this.silentErrors}
				ref={(element) => (this.contentContainerElement = element)}
			>
				<slot slot="content-start" name="content-start" />
				<slot slot="input-start" name="input-start" />
				<div slot="input">{this.renderInput()}</div>
				<slot slot="input-end" name="input-end" />
				<slot slot="content-end" name="content-end" />
			</zds-input-content-container>
		);
	}

	renderCounter() {
		if (!this.maxlength) return;

		return (
			<zds-input-counter
				length={this.value.length}
				maxlength={this.maxlength}
				warnThreshold={this.warnThreshold}
				showThreshold={this.showThreshold}
			></zds-input-counter>
		);
	}

	renderError() {
		if (getVisualValidityStatus(this) === VisualValidityStatus.Valid || this.silentErrors) return null;

		return (
			<zds-form-error
				aria-live="assertive"
				role="alert"
				class="zds-input-text__error"
				id={`${this.zdsId}-feedback`}
			>
				{this.validationState.message}
			</zds-form-error>
		);
	}

	renderInput() {
		const normalInputClasses = generateCssClasses('zds-input-text__input', {
			plaintext: this.plaintext,
		});
		const inputClasses = [normalInputClasses, `form-control${this.plaintext ? '-plaintext' : ''}`].join(' ');

		const inputAriaProps = processAriaObject(this.inputAria, 'zds-input-text', 'input-aria');

		return (
			<input
				aria-describedby={
					getVisualValidityStatus(this) === VisualValidityStatus.Valid ? false : `${this.zdsId}-feedback`
				}
				aria-invalid={getVisualValidityStatus(this) !== VisualValidityStatus.Valid}
				aria-label={this.label}
				autocomplete={this.autocomplete}
				class={inputClasses}
				disabled={this.disabled || undefined}
				id={`${this.zdsId}-input`}
				inputmode={this.inputmode}
				name={this.name}
				pattern={this.pattern}
				onBlur={(event: FocusEvent) => void this.onBlur(event)}
				onChange={(event: Event) => void this.onChange(event)}
				onClick={this.onClick}
				onFocus={this.onFocus}
				onInput={(event: InputEvent) => void this.onInput(event)}
				onKeyDown={this.onKeyDown}
				placeholder={this.placeholder}
				readonly={this.readonly || undefined}
				ref={(element) => (this.inputElement = element)}
				required={this.required || undefined}
				tabIndex={this.componentTabIndex}
				type={this.type}
				value={this.viewValue}
				{...inputAriaProps}
			/>
		);
	}

	inputDidUpdate() {
		this.zdsInputDidUpdate.emit({
			id: this.zdsId,
			includeInForm: this.includeInForm,
		});
	}

	updateViewValue(applyFormatters = true) {
		if (this.value === undefined) {
			return;
		}

		// do not format the value if the component has focus
		if (this.hostElement.shadowRoot?.activeElement === this.inputElement) {
			applyFormatters = false;
		}

		let viewValue = this.value;

		if (applyFormatters) {
			this.viewValueFormatters.forEach((formatter) => (viewValue = formatter(viewValue)));
		}

		this.viewValue = this.inputElement!.value = viewValue;
	}
}
